import { Apple, Smartphone } from 'lucide-react';
import { AppleTvIcon } from './AppleTvIcon';
import { IosIcon } from './IosIcon';
import { Iphone13Icon } from './Iphone13Icon';
import { Iphone14ProIcon } from './Iphone14ProIcon';
import { Iphone15ProIcon } from './Iphone15ProIcon';
import { Iphone15ProMaxIcon } from './Iphone15ProMaxIcon';
import { IphoneSeIcon } from './IphoneSeIcon';
import { IpadIcon } from './IpadIcon';
import { IpadProIcon } from './IpadProIcon';
import { IpadAirIcon } from './IpadAirIcon';
import { IpadOsIcon } from './IpadOsIcon';
import { AppleWatch789Icon } from './AppleWatch789Icon';
import { AppleWatchUltraIcon } from './AppleWatchUltraIcon';
import { AppleWatchNikeIcon } from './AppleWatchNikeIcon';
import { AppleWatchSideIcon } from './AppleWatchSideIcon';
import { AppleWatchDockIcon } from './AppleWatchDockIcon';
import { WatchBandIcon } from './WatchBandIcon';
import { WatchosIcon } from './WatchosIcon';
import { AppleFitnessIcon } from './AppleFitnessIcon';
import { VisionProOutlineIcon } from './VisionProOutlineIcon';
import { VisionProSolidIcon } from './VisionProSolidIcon';
import { VisionProIcon } from '../VisionProIcon';
import { MacbookProIcon } from './MacbookProIcon';
import { MacbookAirIcon } from './MacbookAirIcon';
import { MacbookPro2020Icon } from './MacbookPro2020Icon';
import { Imac2020Icon } from './Imac2020Icon';
import { Imac2021Icon } from './Imac2021Icon';
import { MacMiniIcon } from './MacMiniIcon';
import { MacStudioIcon } from './MacStudioIcon';
import { MacProIcon } from './MacProIcon';
import { ProDisplayXdrIcon } from './ProDisplayXdrIcon';
import { StudioDisplayIcon } from './StudioDisplayIcon';
import { MacOsIcon } from './MacOsIcon';

interface AppleDeviceIconProps {
  osType: string;
  variant?: 
    // iPhone variants
    | 'iphone-15-pro-max' | 'iphone-15-pro' | 'iphone-14-pro' 
    | 'iphone-13' | 'iphone-se' | 'ios-branded'
    // iPad variants
    | 'standard' | 'branded' | 'pro' | 'air' | 'pro-back' | 'ipados-branded'
    // Mac variants
    | 'macbook-pro' | 'macbook-air' | 'macbook-pro-2020'
    | 'imac-2020' | 'imac-2021'
    | 'mac-mini' | 'mac-studio' | 'mac-pro'
    | 'studio-display' | 'pro-display-xdr' | 'macos-branded'
    // Apple Watch variants
    | 'watch-7-8-9' | 'watch-ultra' | 'watch-nike' | 'watch-side'
    | 'watch-dock' | 'watch-band' | 'watchos-branded' | 'apple-fitness'
    // Vision Pro variants
    | 'vision-pro-outline' | 'vision-pro-solid';
  className?: string;
  modelName?: string;
}

export const AppleDeviceIcon = ({ 
  osType, 
  variant,
  className = "h-5 w-5",
  modelName
}: AppleDeviceIconProps) => {
  const iconProps = { className };
  
  switch (osType.toLowerCase()) {
    case 'macos':
      // Use explicit variant if provided, otherwise auto-detect
      const macVariant = variant || detectMacVariant(modelName);
      
      // Branded macOS icon for generic OS entries
      if (macVariant === 'macos-branded') return <MacOsIcon {...iconProps} />;
      
      // Laptops
      if (macVariant === 'macbook-pro') return <MacbookProIcon {...iconProps} />;
      if (macVariant === 'macbook-air') return <MacbookAirIcon {...iconProps} />;
      if (macVariant === 'macbook-pro-2020') return <MacbookPro2020Icon {...iconProps} />;
      
      // Desktops
      if (macVariant === 'imac-2021') return <Imac2021Icon {...iconProps} />;
      if (macVariant === 'imac-2020') return <Imac2020Icon {...iconProps} />;
      if (macVariant === 'mac-mini') return <MacMiniIcon {...iconProps} />;
      if (macVariant === 'mac-studio') return <MacStudioIcon {...iconProps} />;
      if (macVariant === 'mac-pro') return <MacProIcon {...iconProps} />;
      
      // Displays
      if (macVariant === 'studio-display') return <StudioDisplayIcon {...iconProps} />;
      if (macVariant === 'pro-display-xdr') return <ProDisplayXdrIcon {...iconProps} />;
      
      // Default fallback
      return <Apple {...iconProps} />;
      
    case 'ios':
      const iosVariant = variant || detectIosVariant(modelName);
      
      // Use branded iOS icon for generic OS entries
      if (iosVariant === 'ios-branded') return <IosIcon {...iconProps} />;
      
      // Specific iPhone models
      if (iosVariant === 'iphone-15-pro-max') return <Iphone15ProMaxIcon {...iconProps} />;
      if (iosVariant === 'iphone-15-pro') return <Iphone15ProIcon {...iconProps} />;
      if (iosVariant === 'iphone-14-pro') return <Iphone14ProIcon {...iconProps} />;
      if (iosVariant === 'iphone-13') return <Iphone13Icon {...iconProps} />;
      if (iosVariant === 'iphone-se') return <IphoneSeIcon {...iconProps} />;
      
      // Fallback to generic smartphone icon for unknown iPhone models
      return <Smartphone {...iconProps} />;
      
    case 'ipados':
      const ipadVariant = variant || detectIpadVariant(modelName);
      
      // Use branded iPadOS icon for generic OS entries
      if (ipadVariant === 'ipados-branded' || ipadVariant === 'branded') return <IpadOsIcon {...iconProps} />;
      
      // Specific iPad models
      if (ipadVariant === 'pro') return <IpadProIcon {...iconProps} />;
      if (ipadVariant === 'air') return <IpadAirIcon {...iconProps} />;
      
      return <IpadIcon {...iconProps} />;
      
    case 'watchos':
      const watchVariant = variant || detectAppleWatchModel(modelName);
      
      if (watchVariant === 'watchos-branded') return <WatchosIcon {...iconProps} />;
      if (watchVariant === 'watch-ultra') return <AppleWatchUltraIcon {...iconProps} />;
      if (watchVariant === 'watch-nike') return <AppleWatchNikeIcon {...iconProps} />;
      if (watchVariant === 'watch-7-8-9') return <AppleWatch789Icon {...iconProps} />;
      if (watchVariant === 'watch-side') return <AppleWatchSideIcon {...iconProps} />;
      if (watchVariant === 'watch-dock') return <AppleWatchDockIcon {...iconProps} />;
      if (watchVariant === 'watch-band') return <WatchBandIcon {...iconProps} />;
      if (watchVariant === 'apple-fitness') return <AppleFitnessIcon {...iconProps} />;
      
      return <AppleWatch789Icon {...iconProps} />;
      
    case 'tvos':
      return <AppleTvIcon {...iconProps} />;
      
    case 'visionos':
      if (variant === 'vision-pro-solid') return <VisionProSolidIcon {...iconProps} />;
      if (variant === 'vision-pro-outline') return <VisionProOutlineIcon {...iconProps} />;
      return <VisionProOutlineIcon {...iconProps} />;
      
    default:
      return <Apple {...iconProps} />;
  }
};

const detectMacVariant = (modelName?: string): string | undefined => {
  if (!modelName) return undefined;
  
  const name = modelName.toLowerCase();
  
  // MacBook detection
  if (name.includes('macbook pro')) return 'macbook-pro';
  if (name.includes('macbook air')) return 'macbook-air';
  
  // iMac detection
  if (name.includes('imac')) {
    // Try to detect year if possible
    if (name.includes('2021') || name.includes('m1')) return 'imac-2021';
    if (name.includes('2020')) return 'imac-2020';
    return 'imac-2021'; // Default to newer model
  }
  
  // Mac Mini, Studio, Pro
  if (name.includes('mac mini')) return 'mac-mini';
  if (name.includes('mac studio')) return 'mac-studio';
  if (name.includes('mac pro')) return 'mac-pro';
  
  // Displays
  if (name.includes('studio display')) return 'studio-display';
  if (name.includes('pro display') || name.includes('xdr')) return 'pro-display-xdr';
  
  // Detect macOS version names for generic OS entries
  if (name.includes('macos') || name.includes('os x')) {
    // Use branded macOS icon for generic OS entries
    return 'macos-branded';
  }
  
  return undefined; // Return undefined to use fallback Apple icon
};

const detectIosVariant = (modelName?: string): string | undefined => {
  if (!modelName) return undefined;
  
  const name = modelName.toLowerCase();
  
  // Detect specific iPhone models first
  if (name.includes('iphone 15 pro max')) return 'iphone-15-pro-max';
  if (name.includes('iphone 15 pro')) return 'iphone-15-pro';
  if (name.includes('iphone 14 pro')) return 'iphone-14-pro';
  if (name.includes('iphone 13')) return 'iphone-13';
  if (name.includes('iphone se')) return 'iphone-se';
  
  // Detect generic iOS version names (e.g., "iOS 18.1", "iOS 17.5")
  // Use word boundary or space to avoid matching "iPadOS"
  if ((name.includes('ios ') || name.match(/\bios\b/)) && !name.includes('iphone') && !name.includes('ipados')) {
    return 'ios-branded';
  }
  
  return undefined;
};

const detectIpadVariant = (modelName?: string): string | undefined => {
  if (!modelName) return undefined;
  
  const name = modelName.toLowerCase();
  
  // Detect specific iPad models first
  if (name.includes('ipad pro')) return 'pro';
  if (name.includes('ipad air')) return 'air';
  
  // Detect generic iPadOS version names (e.g., "iPadOS 17.5", "iPadOS 16.7")
  // Check for "ipados" specifically (not just "ios")
  if (name.includes('ipados') && !name.includes('ipad pro') && !name.includes('ipad air')) {
    return 'ipados-branded';
  }
  
  return undefined;
};

const detectAppleWatchModel = (modelName?: string): string => {
  if (!modelName) return 'watch-7-8-9';
  
  const name = modelName.toLowerCase();
  
  // Detect generic watchOS version names (e.g., "watchOS 11.0", "watchOS 10.5")
  if (name.includes('watchos') && !name.includes('apple watch')) {
    return 'watchos-branded';
  }
  
  // Detect specific watch models
  if (name.includes('apple watch ultra')) return 'watch-ultra';
  if (name.includes('apple watch nike')) return 'watch-nike';
  if (name.includes('apple watch series 9')) return 'watch-7-8-9';
  if (name.includes('apple watch series 8')) return 'watch-7-8-9';
  if (name.includes('apple watch series 7')) return 'watch-7-8-9';
  if (name.includes('apple watch')) return 'watch-7-8-9';
  if (name.includes('fitness')) return 'apple-fitness';
  
  return 'watch-7-8-9';
};
